import React, {useEffect, useMemo, useState} from "react";
import {useTranslate} from "@tolgee/react";
import {
  useFormActionsContext,
  useFormStateContext,
} from "view/SignUp/SignUpToken/context";
import {useForm, useWatch} from "react-hook-form";
import Form, {BasicInput, BasicSelect, InputGroup} from "view/components/Form";
import ButtonGroup from "view/SignUp/SignUpToken/components/ButtonGroup";
import {phoneValidationPattern} from "tools/validations";
import StandSizes from "view/SignUp/SignUpToken/components/StandSizes";
import {debounce} from "lodash";
import {apiV2} from "api/firebase";
import {getErrorMessage} from "tools/error";
import {StandDetailsFormState} from "view/SignUp/SignUpToken/context/types";
import FlexBox from "view/components/Layout/FlexBox";
import useLocalizedLocalMap from "hooks/useLocalizedLocalMap";
import {deepTrimValue} from "tools/common/trimFormValues";
import {getBuildTypeOptions} from "view/SignUp/SignUpToken/components/StandDetails/helpers";
import {useParams} from "react-router-dom";
import Alert from "view/components/Alert";

const StandDetails = () => {
  const {t} = useTranslate();
  const {uid: token} = useParams();
  const [error, setError] = useState<string>("");
  const {
    standDetails: defaultValues,
    show,
    validationResult,
    credentials,
    googleIdentity,
  } = useFormStateContext();
  const {
    setStandDetails,
    setSignUpResult,
    nextStep,
    completeGoogleSignUp,
  } = useFormActionsContext();
  const localMap = useLocalizedLocalMap(show);

  const form = useForm<StandDetailsFormState>({
    defaultValues,
    mode: "all",
  });

  const {handleSubmit, formState, control} = form;
  const formValues = useWatch({control, defaultValue: defaultValues});

  const debouncedSetFormData = useMemo(() => debounce(setStandDetails, 1000), [
    setStandDetails,
  ]);
  const {isSubmitting, disabled, isValid} = formState;
  const onSubmit = async (data: StandDetailsFormState) => {
    if (!validationResult || !token) {
      return;
    }
    setError("");
    const trimmedFormValues: StandDetailsFormState = deepTrimValue(data);
    const response = await apiV2.embeddedShows
      .createExhibitor({
        ...credentials,
        ...trimmedFormValues,
        showId: show.id,
        token,
        identityToken: googleIdentity?.identityToken,
      })
      .catch((e: unknown) => {
        console.error("Error signing exhibitor up", e);
        setError(getErrorMessage(e));
      });
    if (!response) {
      return;
    }
    setSignUpResult(response);

    // With Google there is no invitation email to wait for — the account is
    // already active, so the person goes straight into the portal instead of
    // to the "check your inbox" page.
    if (googleIdentity) {
      await completeGoogleSignUp();
      return;
    }

    nextStep();
  };

  // NOTE: Persist form state between steps
  useEffect(() => {
    debouncedSetFormData(formValues as StandDetailsFormState);
  }, [formValues, debouncedSetFormData]);

  useEffect(() => () => debouncedSetFormData.flush(), [debouncedSetFormData]);

  const localizedPostCode = localMap.postCode;
  const buildTypes = useMemo(() => getBuildTypeOptions(t, show?.buildTypes), [
    t,
    show,
  ]);

  const isSubmitDisabled =
    isSubmitting || disabled || !isValid || !buildTypes[0].value;

  return (
    <Form form={form} onSubmit={handleSubmit(onSubmit)} style={{padding: 0}}>
      <FlexBox column gap={1} width="100%">
        <InputGroup>
          <BasicInput
            label={t("global.sign_up.first_name")}
            name="firstName"
            id="firstName"
            required
            rules={{
              required: true,
            }}
          />
          <BasicInput
            label={t("global.sign_up.last_name")}
            name="lastName"
            id="lastName"
            required
            rules={{
              required: true,
            }}
          />
        </InputGroup>
        <BasicInput
          label={t("global.sign_up.registered_address")}
          name="companyAddress"
          id="registerAddress"
          required={false}
          rules={{
            required: false,
            maxLength: 150,
          }}
        />
        <InputGroup>
          <BasicInput
            label={localizedPostCode}
            id="postcode"
            name="companyPostcode"
            uppercase
            required={false}
            rules={{
              required: false,
              maxLength: 10,
            }}
          />
          <BasicInput
            label={t("global.sign_up.contact_phone_number")}
            name="contactNumber"
            id="contactNumber"
            required={false}
            rules={{
              required: false,
              pattern: {
                value: phoneValidationPattern,
                message: t("common.validation.phone"),
              },
            }}
          />
        </InputGroup>
        <BasicSelect
          name="buildType"
          required
          label={t("global.sign_up.build_type")}
          options={buildTypes}
        />
        <StandSizes form={form} unitSystem={show.unitSystem} />
        {!!error && <Alert severity="error">{error}</Alert>}
        <ButtonGroup
          isSubmitDisabled={isSubmitDisabled}
          isSubmitting={isSubmitting}
        />
      </FlexBox>
    </Form>
  );
};

export default StandDetails;
